<template>
  <div class="pricing">
    <div v-if="loading">loading ...</div>
    <div v-if="isOwner && !preview" class="stripe-connect">
      <ff-loading :loading="loadingStripe" :text="'Loading your Stripe configuration'" v-if="loadingStripe"/>
      <div class="pricing-default" v-if="!stripeConnected && !loadingStripe">
        <p>Connecting with Stripe is how we get you paid. Without connecting with Stripe, you can't get paid, because we don't have the information necessary to pay you.</p>
        <p>Please connect now to continue.</p>
        <connect-stripe :text="'Connect with Stripe'"/>
      </div>
      <div class="pricing-default is-important" v-else-if="stripePending">
        <p>From our end, your Stripe account is all connected, but they need a bit more time to get their ducks in a row. Please check back later.</p>
      </div>
      <div class="pricing-default is-important" v-else-if="stripeNeedsMoreInfo">
        <p>It looks like you started the process of connecting with Stripe, but they need some more info from you. Please click the button below to continue that process.</p>
        <p>Connecting with Stripe is how we get you paid. Without connecting with Stripe, you can't get paid, because we don't have the information necessary to pay you.</p>
        <connect-stripe :text="'Connect with Stripe'" :url="stripeUrl"/>
      </div>
      <div class="pricing-edit" v-else>
          <aside class="pricing-aside">
            You can set a monthly subscription price for your users. On each page, you can select whether you want it to be free or paid content.
          </aside>
          <div class="pricing-table__tier">
            <ff-input v-model="deft.name" :label="'Tier Name'" :action="onChange"/>
            <ff-input v-model="deft.price" :label="'Monthly Price to Customers'" :action="onPriceChange"/>
            <aside class="pricing-warn" v-if="priceChanged">You're about to change the price. If you have subscribed customers, this will only affect their subscription if the new price is lower.</aside>
            <ff-input v-model="deft.description" :label="'Description of Tier'" :action="onChange"/>
            <ff-button v-if="tierChanged" :action="saveTier" :text="'Save'"/>
            <ff-button v-if="noTiers" :action="saveTier" :text="'Turn on Payments'"/>
            <span v-if="saved">Saved!</span>
          </div>
      </div>
    </div>
  </div>
</template>

<script>
import firebase from 'firebase/app'
import 'firebase/functions'

import { mapState } from 'vuex'

import persist from '../../mixins/persist'
import FFInput from '../DesignSystem/FFInput'
import FFButton from '../DesignSystem/FFButton'
import FFLoading from '../DesignSystem/FFLoading'

import ConnectStripe from '../_Shared/ConnectStripe'

const DEFAULT_TIER = 'paid'

export default {
  name: 'Pricing',
  props: {
  },
  mixins: [persist],
  components: {
    'connect-stripe': ConnectStripe,
    'ff-input': FFInput,
    'ff-button': FFButton,
    'ff-loading': FFLoading
  },
  data () {
    return {
      activeTier: '',
      tierChanged: false,
      priceChanged: false,
      saved: false,
      deft: {
        name: DEFAULT_TIER,
        price: '9.99',
        description: 'Get unlimited content on this plan.'
      },
      stripeConnected: false,
      stripePending: false,
      stripeNeedsMoreInfo: false,
      stripeUrl: '',
      loadingStripe: false
    }
  },
  computed: {
    ...mapState(['user', 'channel', 'channel_id']),
    loading () {
      return !this.user || !this.channel
    },
    noTiers () {
      return !this.channel.tiers
    },
    preview () {
      return this.$route.query.preview
    },
    isOwner () {
      return this.$store.state.channel.isOwner
    }
  },
  methods: {
    onChange () {
      this.saved = false
      this.tierChanged = true
    },
    onPriceChange () {
      this.saved = false
      this.tierChanged = true
      this.priceChanged = true
    },
    setDefault () {
      if (!this.channel.tiers || !this.channel.tiers[DEFAULT_TIER]) return
      let data = {}
      Object.assign(data, this.channel.tiers[DEFAULT_TIER])
      data.price = data.price.toString()
      data.name = data.displayName
      this.deft = data
    },
    saveTier() {
      if (this.deft.name === 'private' || this.deft.name === 'public') return

      let tierName = this.deft.name
      let formattedTier = this.formatTier(this.deft)
      let updateObj = {}

      // Saving a default tier "paid" no matter what
      // in the future we can add more tiers
      updateObj[`tiers.${DEFAULT_TIER}`] = formattedTier

      return this.ChannelUpdate(this.channel_id, updateObj, () => {
        this.deft.name = tierName
        this.$store.dispatch('fetchChannel')
        this.saved = true
        this.tierChanged = false
        this.priceChanged = false
        setTimeout(() => this.saved = false, 3000)
      })
    },
    formatTier (data) {
      let updateObj = {}
      Object.assign(updateObj, data)
      delete updateObj.name
      let floatPrice = parseFloat(data.price).toFixed(2)
      if (isNaN(floatPrice)) return
      floatPrice = Math.round(floatPrice*100)/100
      updateObj.price = floatPrice
      updateObj.displayName = data.name
      return updateObj
    }
  },
  async created() {
    this.setDefault()

    if (this.$store.state.channel.stripeAccountId) {
      this.loadingStripe = true
      const func = firebase.functions().httpsCallable('stripe-validateStripe')
      const response = await func({returnUrl: window.location.origin + '/settings'});

      if (!response.data.valid) {
        this.loadingStripe = false
        if (response.data.url) {
          this.stripeNeedsMoreInfo = true
          this.stripeConnected = true
          this.stripeUrl = response.data.url
        } else if (!response.data.exists) {
          this.stripeConnected = false
        } else {
          this.stripePending = true
          this.stripeConnected = true
        }
      } else {
        this.loadingStripe = false
        this.stripeConnected = true
      }
    } else {
      this.loadingStripe = false
      this.stripeConnected = false
    }
  }
}
</script>

<style scoped lang="scss">
  @import "../../assets/styles/app.scss";

  .payment {
    width: 500px;
    margin: auto;
  }

  .pricing-aside {
    font-size: $fontSize-normal;
    margin: $unit-1 0;
  }

  .pricing-warn {
    @include aside;
    color: $color-warn;
    padding: 1rem;
  }

  p {
    font-size: $fontSize-normal;
    line-height: $lineHeight;
    margin-bottom: $unit-2;
  }

  .is-important {
    @include callout("⚠️");
  }

</style>
